import { AdminRole } from "@repo/api-dtos"
import type { CatalogueLookupHit } from "@repo/game-admin"
import type { LookupHit } from "@repo/marketplace-admin"
import {
	Ban,
	Barcode,
	Box,
	Boxes,
	BrainCircuit,
	Copy,
	CreditCard,
	ExternalLink,
	FileSpreadsheet,
	FlaskConical,
	Gamepad2,
	GitCompareArrows,
	Handshake,
	Layers,
	Plug,
	ShieldAlert,
	ShoppingBag,
	Star,
	Ticket,
	Users,
} from "lucide-react"
import { toast } from "sonner"
import { z } from "zod"
import {
	API_USAGE_SEARCH_DEFAULTS,
	MODERATION_SEARCH_DEFAULTS,
	OFFERS_SEARCH_DEFAULTS,
	ORDERS_SEARCH_DEFAULTS,
	PRODUCTS_SEARCH_DEFAULTS,
	TRANSACTIONS_SEARCH_DEFAULTS,
} from "@/config/search-defaults"
import {
	cardmarketProductUrl,
	integrationConnectUrl,
	marketplaceProductUrl,
	publicProfileUrl,
	stripeDashboardUrl,
	tcgplayerProductUrl,
} from "./external-links"
import {
	confirmStep,
	type PaletteCommand,
	type PaletteContext,
	type PaletteStep,
} from "./types"

const HIT_PRECEDENCE = [
	"game",
	"integrationApp",
	"user",
	"order",
	"transaction",
	"negotiation",
	"moderationCase",
	"coupon",
	"expansion",
	"expansionGroup",
	"product",
	"reviewAppeal",
	"ipBan",
	"monthlyClose",
	"scannerModel",
	"scannerJob",
	"aiMatcherRun",
	"proposal",
] as const

export function hitPrecedence(type: string): number {
	const index = (HIT_PRECEDENCE as readonly string[]).indexOf(type)
	return index === -1 ? HIT_PRECEDENCE.length : index
}

const EXACT_MARKETPLACE_MATCHES = new Set<LookupHit["matchedBy"]>([
	"id",
	"orderNumber",
	"transactionNumber",
	"clerkUserId",
	"stripeAccount",
	"stripeCustomer",
	"stripeCheckoutSession",
	"stripePaymentIntent",
	"stripeCharge",
	"slug",
	"ip",
	"cidr",
])

const EXACT_CATALOGUE_MATCHES = new Set<CatalogueLookupHit["matchedBy"]>([
	"id",
	"slug",
	"publicId",
	"tcgplayerId",
	"cardmarketId",
])

/**
 * Whether a marketplace hit is the very thing typed (an id, an order or
 * transaction number, a user's full email or username), as opposed to a
 * partial name match. `value` is the classified query.
 */
export function isExactMarketplaceHit(hit: LookupHit, value: string): boolean {
	if (EXACT_MARKETPLACE_MATCHES.has(hit.matchedBy)) {
		return true
	}

	if (hit.type !== "user") {
		return false
	}

	const typed = value.toLowerCase()

	return (
		hit.username.toLowerCase() === typed || hit.email.toLowerCase() === typed
	)
}

/** Whether a catalogue hit was found by an identifier rather than by its name. */
export function isExactCatalogueHit(hit: CatalogueLookupHit): boolean {
	return EXACT_CATALOGUE_MATCHES.has(hit.matchedBy)
}

function formatCents(cents: number, currency: string): string {
	try {
		return new Intl.NumberFormat(undefined, {
			style: "currency",
			currency,
		}).format(cents / 100)
	} catch {
		return `${(cents / 100).toFixed(2)} ${currency}`
	}
}

function copyCommand(id: string, label: string, value: string): PaletteCommand {
	return {
		id,
		label,
		description: value,
		icon: Copy,
		group: "results",
		perform: async (ctx) => {
			try {
				await navigator.clipboard.writeText(value)
				toast.success(`${label.replace(/^Copy /, "")} copied`)
			} catch {
				toast.error("Could not copy to clipboard")
			}
			ctx.close()
		},
	}
}

function linkCommand(id: string, label: string, href: string): PaletteCommand {
	return {
		id,
		label: `${label} ↗`,
		description: href,
		icon: ExternalLink,
		group: "results",
		perform: (ctx) => {
			window.open(href, "_blank", "noopener")
			ctx.close()
		},
	}
}

function step(title: string, commands: PaletteCommand[]): PaletteStep {
	return { title, commands }
}

export function stripeOtherCommand(stripeId: string): PaletteCommand {
	return linkCommand(
		`stripe:${stripeId}`,
		"Open in Stripe",
		stripeDashboardUrl(stripeId),
	)
}

export function marketplaceHitToCommand(hit: LookupHit): PaletteCommand {
	switch (hit.type) {
		case "user": {
			const open = (ctx: PaletteContext, tab?: string) => {
				ctx.navigate({
					to: "/users/$userId",
					params: { userId: hit.id },
					search: tab ? { tab: tab as never } : {},
				})
				ctx.close()
			}
			return {
				id: `hit:user:${hit.id}`,
				label: `@${hit.username}`,
				description: `${hit.email}${hit.isSeller ? " · seller" : ""}`,
				icon: Users,
				group: "results",
				perform: (ctx) => open(ctx),
				submenu: () =>
					step(`@${hit.username}`, [
						{
							id: `hit:user:${hit.id}:open`,
							label: "Open",
							group: "results",
							perform: (ctx) => open(ctx),
						},
						...(["orders", "reviews", "json"] as const).map(
							(tab): PaletteCommand => ({
								id: `hit:user:${hit.id}:tab:${tab}`,
								label: `Open › ${tab === "json" ? "JSON" : tab[0]?.toUpperCase() + tab.slice(1)}`,
								group: "results",
								perform: (ctx) => open(ctx, tab),
							}),
						),
						{
							id: `hit:user:${hit.id}:tab:network`,
							label: "Open › Network",
							group: "results",
							requiredRoles: [AdminRole.MarketplaceUserManagement],
							perform: (ctx) => open(ctx, "network"),
						},
						{
							id: `hit:user:${hit.id}:tab:api-usage`,
							label: "Open › API usage",
							group: "results",
							requiredRoles: [AdminRole.IntegrationsAdmin],
							perform: (ctx) => open(ctx, "api-usage"),
						},
						{
							id: `hit:user:${hit.id}:orders`,
							label: "Orders as buyer or seller",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/orders",
									search: {
										...ORDERS_SEARCH_DEFAULTS,
										search: `@${hit.username}`,
									},
								})
								ctx.close()
							},
						},
						{
							id: `hit:user:${hit.id}:offers`,
							label: "Offers",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/offers",
									search: {
										...OFFERS_SEARCH_DEFAULTS,
										search: `@${hit.username}`,
									},
								})
								ctx.close()
							},
						},
						{
							id: `hit:user:${hit.id}:transactions`,
							label: "Transactions as buyer",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/transactions",
									search: {
										...TRANSACTIONS_SEARCH_DEFAULTS,
										search: `@${hit.username}`,
									},
								})
								ctx.close()
							},
						},
						{
							id: `hit:user:${hit.id}:moderation`,
							label: "Moderation cases",
							group: "results",
							requiredRoles: [AdminRole.MarketplaceUserManagement],
							perform: (ctx) => {
								ctx.navigate({
									to: "/moderation",
									search: {
										...MODERATION_SEARCH_DEFAULTS,
										userId: hit.id,
										status: "all",
									},
								})
								ctx.close()
							},
						},
						{
							id: `hit:user:${hit.id}:api-usage-account`,
							label: "Public API usage",
							group: "results",
							requiredRoles: [AdminRole.IntegrationsAdmin],
							perform: (ctx) => {
								ctx.navigate({
									to: "/api-usage",
									search: { ...API_USAGE_SEARCH_DEFAULTS, accountId: hit.id },
								})
								ctx.close()
							},
						},
						linkCommand(
							`hit:user:${hit.id}:profile`,
							"Public profile",
							publicProfileUrl(hit.username),
						),
						...(hit.stripeAccountId
							? [
									linkCommand(
										`hit:user:${hit.id}:stripe`,
										"Stripe Connect",
										stripeDashboardUrl(hit.stripeAccountId),
									),
								]
							: []),
						copyCommand(`hit:user:${hit.id}:copy-id`, "Copy user id", hit.id),
						copyCommand(
							`hit:user:${hit.id}:copy-username`,
							"Copy username",
							hit.username,
						),
						copyCommand(
							`hit:user:${hit.id}:copy-email`,
							"Copy email",
							hit.email,
						),
					]),
			}
		}

		case "order": {
			const open = (ctx: PaletteContext, tab?: string) => {
				ctx.navigate({
					to: "/orders/$orderId",
					params: { orderId: hit.orderNumber || hit.id },
					search: { tab: tab as never },
				})
				ctx.close()
			}
			const tabs = [
				["risk", "Risk"],
				["order-chat", "Order chat"],
				["buyer-support", "Buyer support"],
				["seller-support", "Seller support"],
				["full-json", "JSON"],
			] as const
			return {
				id: `hit:order:${hit.id}`,
				label: hit.orderNumber,
				description: `${hit.status} · ${hit.buyerUsername ?? "?"} → ${hit.sellerUsername ?? "?"} · ${formatCents(hit.subtotal, hit.currency)}`,
				icon: ShoppingBag,
				group: "results",
				perform: (ctx) => open(ctx),
				submenu: () =>
					step(hit.orderNumber, [
						{
							id: `hit:order:${hit.id}:open`,
							label: "Open",
							group: "results",
							perform: (ctx) => open(ctx),
						},
						...tabs.map(
							([tab, label]): PaletteCommand => ({
								id: `hit:order:${hit.id}:tab:${tab}`,
								label: `Open › ${label}`,
								group: "results",
								perform: (ctx) => open(ctx, tab),
							}),
						),
						{
							id: `hit:order:${hit.id}:transaction`,
							label: "Transaction",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/transactions/$transactionId",
									params: { transactionId: hit.transactionId },
								})
								ctx.close()
							},
						},
						{
							id: `hit:order:${hit.id}:buyer`,
							label: `Buyer${hit.buyerUsername ? ` @${hit.buyerUsername}` : ""}`,
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/users/$userId",
									params: { userId: hit.buyerId },
								})
								ctx.close()
							},
						},
						{
							id: `hit:order:${hit.id}:seller`,
							label: `Seller${hit.sellerUsername ? ` @${hit.sellerUsername}` : ""}`,
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/users/$userId",
									params: { userId: hit.sellerId },
								})
								ctx.close()
							},
						},
						copyCommand(
							`hit:order:${hit.id}:copy-number`,
							"Copy order number",
							hit.orderNumber,
						),
						copyCommand(`hit:order:${hit.id}:copy-id`, "Copy id", hit.id),
					]),
			}
		}

		case "transaction": {
			const open = (ctx: PaletteContext, tab?: string) => {
				ctx.navigate({
					to: "/transactions/$transactionId",
					params: { transactionId: hit.transactionNumber || hit.id },
					search: { tab: tab as never },
				})
				ctx.close()
			}
			const stripeId = hit.stripeChargeId ?? hit.stripeCheckoutSessionId
			return {
				id: `hit:transaction:${hit.id}`,
				label: hit.transactionNumber,
				description: `${hit.status} · ${hit.buyerUsername ?? "?"} · ${formatCents(hit.total, hit.currency)}`,
				icon: CreditCard,
				group: "results",
				perform: (ctx) => open(ctx),
				submenu: () =>
					step(hit.transactionNumber, [
						{
							id: `hit:transaction:${hit.id}:open`,
							label: "Open",
							group: "results",
							perform: (ctx) => open(ctx),
						},
						{
							id: `hit:transaction:${hit.id}:json`,
							label: "Open › JSON",
							group: "results",
							perform: (ctx) => open(ctx, "full-json"),
						},
						{
							id: `hit:transaction:${hit.id}:buyer`,
							label: `Buyer${hit.buyerUsername ? ` @${hit.buyerUsername}` : ""}`,
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/users/$userId",
									params: { userId: hit.buyerId },
								})
								ctx.close()
							},
						},
						...(stripeId
							? [
									linkCommand(
										`hit:transaction:${hit.id}:stripe`,
										"Stripe payment",
										stripeDashboardUrl(stripeId),
									),
								]
							: []),
						copyCommand(
							`hit:transaction:${hit.id}:copy-number`,
							"Copy TX number",
							hit.transactionNumber,
						),
						copyCommand(`hit:transaction:${hit.id}:copy-id`, "Copy id", hit.id),
					]),
			}
		}

		case "negotiation": {
			const open = (ctx: PaletteContext, tab?: string) => {
				ctx.navigate({
					to: "/offers/$negotiationId",
					params: { negotiationId: hit.id },
					search: { tab: tab as never },
				})
				ctx.close()
			}
			return {
				id: `hit:negotiation:${hit.id}`,
				label: `Offer ${hit.id.slice(-8)}`,
				description: `${hit.status} · ${hit.buyerUsername ?? "?"} → ${hit.sellerUsername ?? "?"} · ${formatCents(hit.latestOfferTotal, hit.currency)}`,
				icon: Handshake,
				group: "results",
				perform: (ctx) => open(ctx),
				submenu: () =>
					step(`Offer ${hit.id.slice(-8)}`, [
						{
							id: `hit:negotiation:${hit.id}:open`,
							label: "Open",
							group: "results",
							perform: (ctx) => open(ctx),
						},
						{
							id: `hit:negotiation:${hit.id}:chat`,
							label: "Open › Chat log",
							group: "results",
							perform: (ctx) => open(ctx, "chat"),
						},
						{
							id: `hit:negotiation:${hit.id}:json`,
							label: "Open › JSON",
							group: "results",
							perform: (ctx) => open(ctx, "full-json"),
						},
						{
							id: `hit:negotiation:${hit.id}:buyer`,
							label: `Buyer${hit.buyerUsername ? ` @${hit.buyerUsername}` : ""}`,
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/users/$userId",
									params: { userId: hit.buyerId },
								})
								ctx.close()
							},
						},
						{
							id: `hit:negotiation:${hit.id}:seller`,
							label: `Seller${hit.sellerUsername ? ` @${hit.sellerUsername}` : ""}`,
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/users/$userId",
									params: { userId: hit.sellerId },
								})
								ctx.close()
							},
						},
						copyCommand(`hit:negotiation:${hit.id}:copy-id`, "Copy id", hit.id),
					]),
			}
		}

		case "moderationCase": {
			const open = (ctx: PaletteContext, tab?: string) => {
				ctx.navigate({
					to: "/moderation/$caseId",
					params: { caseId: hit.id },
					search: { tab: tab as never, conversation: undefined },
				})
				ctx.close()
			}
			return {
				id: `hit:moderationCase:${hit.id}`,
				label: `Moderation case${hit.username ? ` · @${hit.username}` : ""}`,
				description: `${hit.severity} · ${hit.status}${hit.pendingConfirmation ? " · needs confirmation" : ""}`,
				icon: ShieldAlert,
				group: "results",
				requiredRoles: [AdminRole.MarketplaceUserManagement],
				perform: (ctx) => open(ctx),
				submenu: () =>
					step("Moderation case", [
						{
							id: `hit:moderationCase:${hit.id}:open`,
							label: "Open",
							group: "results",
							perform: (ctx) => open(ctx),
						},
						{
							id: `hit:moderationCase:${hit.id}:conversation`,
							label: "Open › Conversation",
							group: "results",
							perform: (ctx) => open(ctx, "conversation"),
						},
						{
							id: `hit:moderationCase:${hit.id}:json`,
							label: "Open › JSON",
							group: "results",
							perform: (ctx) => open(ctx, "json"),
						},
						{
							id: `hit:moderationCase:${hit.id}:user`,
							label: `User${hit.username ? ` @${hit.username}` : ""}`,
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/users/$userId",
									params: { userId: hit.userId },
								})
								ctx.close()
							},
						},
						copyCommand(
							`hit:moderationCase:${hit.id}:copy-id`,
							"Copy id",
							hit.id,
						),
					]),
			}
		}

		case "reviewAppeal":
			return {
				id: `hit:reviewAppeal:${hit.id}`,
				label: `Review appeal · ${hit.orderNumber}`,
				description: `${hit.status} · by @${hit.appellantUsername}`,
				icon: Star,
				group: "results",
				perform: (ctx) => {
					ctx.navigate({
						to: "/orders/$orderId",
						params: { orderId: hit.orderNumber || hit.orderId },
						search: { tab: undefined },
					})
					ctx.close()
				},
			}

		case "coupon": {
			const open = (ctx: PaletteContext, tab?: string) => {
				ctx.navigate({
					to: "/coupons/$couponId",
					params: { couponId: hit.id },
					search: { tab: tab as never },
				})
				ctx.close()
			}
			return {
				id: `hit:coupon:${hit.id}`,
				label: hit.code,
				description: `Coupon · ${hit.couponType} · ${hit.isActive ? "active" : "inactive"}`,
				icon: Ticket,
				group: "results",
				perform: (ctx) => open(ctx),
				submenu: () =>
					step(hit.code, [
						{
							id: `hit:coupon:${hit.id}:open`,
							label: "Open",
							group: "results",
							perform: (ctx) => open(ctx),
						},
						{
							id: `hit:coupon:${hit.id}:redemptions`,
							label: "Open › Redemptions",
							group: "results",
							perform: (ctx) => open(ctx, "redemptions"),
						},
						{
							id: `hit:coupon:${hit.id}:edit`,
							label: "Edit",
							group: "results",
							requiredRoles: [AdminRole.MarketplaceCoupons],
							perform: (ctx) => {
								ctx.navigate({
									to: "/coupons/$couponId/edit",
									params: { couponId: hit.id },
								})
								ctx.close()
							},
						},
						{
							id: `hit:coupon:${hit.id}:duplicate`,
							label: "Duplicate",
							group: "results",
							requiredRoles: [AdminRole.MarketplaceCoupons],
							perform: (ctx) => {
								ctx.navigate({ to: "/coupons/new", search: { from: hit.id } })
								ctx.close()
							},
						},
						copyCommand(
							`hit:coupon:${hit.id}:copy-code`,
							"Copy code",
							hit.code,
						),
						...(hit.isActive
							? [
									{
										id: `hit:coupon:${hit.id}:deactivate`,
										label: "Deactivate…",
										group: "results" as const,
										requiredRoles: [AdminRole.MarketplaceCoupons],
										perform: (ctx: PaletteContext) => {
											ctx.push(
												confirmStep(
													`Deactivate coupon ${hit.code}`,
													async (c) => {
														await c.marketplaceClient.coupons.deactivate({
															id: hit.id,
														})
														c.queryClient.invalidateQueries({
															queryKey: ["command-palette"],
														})
														toast.success(`Coupon ${hit.code} deactivated`)
													},
												),
											)
										},
									},
								]
							: []),
					]),
			}
		}

		case "integrationApp": {
			const open = (ctx: PaletteContext, tab?: string) => {
				ctx.navigate({
					to: "/integration-apps/$appId",
					params: { appId: hit.id },
					search: { tab: tab as never },
				})
				ctx.close()
			}
			const tabs = [
				["credentials", "Credentials"],
				["connections", "Connections"],
				["usage", "Usage"],
				["rate-limits", "Rate limits"],
			] as const
			return {
				id: `hit:integrationApp:${hit.id}`,
				label: hit.name,
				description: `Integration app · ${hit.slug} · ${hit.status}`,
				icon: Plug,
				group: "results",
				requiredRoles: [AdminRole.IntegrationsAdmin],
				perform: (ctx) => open(ctx),
				submenu: (outerCtx) =>
					step(hit.name, [
						{
							id: `hit:integrationApp:${hit.id}:open`,
							label: "Open",
							group: "results",
							perform: (ctx) => open(ctx),
						},
						...tabs.map(
							([tab, label]): PaletteCommand => ({
								id: `hit:integrationApp:${hit.id}:tab:${tab}`,
								label: `Open › ${label}`,
								group: "results",
								perform: (ctx) => open(ctx, tab),
							}),
						),
						...(outerCtx.isSuperadmin
							? [
									{
										id: `hit:integrationApp:${hit.id}:tab:managed`,
										label: "Open › Managed accounts",
										group: "results" as const,
										perform: (ctx: PaletteContext) => open(ctx, "managed"),
									},
								]
							: []),
						{
							id: `hit:integrationApp:${hit.id}:api-usage`,
							label: "Public API usage",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/api-usage",
									search: { ...API_USAGE_SEARCH_DEFAULTS, appId: hit.id },
								})
								ctx.close()
							},
						},
						linkCommand(
							`hit:integrationApp:${hit.id}:connect`,
							"Connect URL",
							integrationConnectUrl(hit.slug),
						),
						copyCommand(
							`hit:integrationApp:${hit.id}:copy-id`,
							"Copy id",
							hit.id,
						),
						copyCommand(
							`hit:integrationApp:${hit.id}:copy-slug`,
							"Copy slug",
							hit.slug,
						),
					]),
			}
		}

		case "ipBan":
			return {
				id: `hit:ipBan:${hit.id}`,
				label: `IP ban · ${hit.cidr}`,
				description: `${hit.active ? "active" : "expired"} · ${hit.reason}`,
				icon: Ban,
				group: "results",
				requiredRoles: [AdminRole.MarketplaceUserManagement],
				perform: (ctx) => {
					ctx.navigate({
						to: "/moderation/ip-bans",
						search: { add: undefined },
					})
					ctx.close()
				},
				submenu: () =>
					step(hit.cidr, [
						{
							id: `hit:ipBan:${hit.id}:open`,
							label: "Open IP bans",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/moderation/ip-bans",
									search: { add: undefined },
								})
								ctx.close()
							},
						},
						{
							id: `hit:ipBan:${hit.id}:accounts`,
							label: "Accounts seen on this IP…",
							group: "results",
							perform: (ctx) => {
								ctx.pushAsync("Accounts on this IP", async () => {
									const address = hit.cidr.split("/")[0] ?? hit.cidr
									const { accounts } =
										await ctx.marketplaceClient.ipBans.accountsOn({
											ip: address,
										})
									if (accounts.length === 0) {
										toast.info("No accounts seen on this IP")
										return []
									}
									return accounts.map((account) => ({
										id: `hit:ipBan:${hit.id}:account:${account.userId}`,
										label: `@${account.username}`,
										group: "results" as const,
										icon: Users,
										perform: (c: PaletteContext) => {
											c.navigate({
												to: "/users/$userId",
												params: { userId: account.userId },
											})
											c.close()
										},
									}))
								})
							},
						},
						...(hit.active
							? [
									{
										id: `hit:ipBan:${hit.id}:lift`,
										label: "Lift ban…",
										group: "results" as const,
										perform: (ctx: PaletteContext) => {
											ctx.push(
												confirmStep(`Lift ban on ${hit.cidr}`, async (c) => {
													await c.marketplaceClient.ipBans.delete({
														id: hit.id,
													})
													c.queryClient.invalidateQueries({
														queryKey: ["command-palette"],
													})
													toast.success(`Ban on ${hit.cidr} lifted`)
												}),
											)
										},
									},
								]
							: []),
					]),
			}

		case "monthlyClose":
			return {
				id: `hit:monthlyClose:${hit.id}`,
				label: `Monthly close · ${hit.region} ${hit.year}-${String(hit.month).padStart(2, "0")}`,
				description: hit.status,
				icon: FileSpreadsheet,
				group: "results",
				requiredRoles: [AdminRole.MarketplaceFinance],
				perform: (ctx) => {
					ctx.navigate({
						to: "/accounting/monthly-closes/$closeId",
						params: { closeId: hit.id },
					})
					ctx.close()
				},
			}

		case "scannerModel":
			return {
				id: `hit:scannerModel:${hit.id}`,
				label: `Scanner model · ${hit.modelType} v${hit.version}`,
				description: hit.live ? "live" : "not live",
				icon: Layers,
				group: "results",
				requiredRoles: [AdminRole.ScannerAdmin],
				perform: (ctx) => {
					ctx.navigate({
						to: "/card-scanner/models/$modelId",
						params: { modelId: hit.id },
					})
					ctx.close()
				},
			}

		case "scannerJob":
			return {
				id: `hit:scannerJob:${hit.id}`,
				label: `Training job · ${hit.jobType}`,
				description: hit.status,
				icon: BrainCircuit,
				group: "results",
				requiredRoles: [AdminRole.ScannerAdmin],
				perform: (ctx) => {
					ctx.navigate({
						to: "/card-scanner/jobs/$jobId",
						params: { jobId: hit.id },
					})
					ctx.close()
				},
			}
	}
}

const ProductionProductSchema = z.object({
	publicId: z.number(),
	nameSlug: z.string(),
	productType: z.string(),
	expansion: z.object({ slug: z.string() }).nullish(),
})

export function catalogueHitToCommand(hit: CatalogueLookupHit): PaletteCommand {
	switch (hit.type) {
		case "game":
			return {
				id: `hit:game:${hit.gameSlug}`,
				label: hit.name ?? hit.gameSlug,
				description: "Game",
				icon: Gamepad2,
				group: "results",
				perform: (ctx) => {
					ctx.navigate({
						to: "/catalogue/games/$gameSlug",
						params: { gameSlug: hit.gameSlug },
					})
					ctx.close()
				},
				submenu: () =>
					step(hit.name ?? hit.gameSlug, [
						{
							id: `hit:game:${hit.gameSlug}:backlog`,
							label: "Backlog",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/catalogue/games/$gameSlug",
									params: { gameSlug: hit.gameSlug },
								})
								ctx.close()
							},
						},
						{
							id: `hit:game:${hit.gameSlug}:coverage`,
							label: "Coverage detail",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({ to: "/games/$id", params: { id: hit.gameSlug } })
								ctx.close()
							},
						},
						{
							id: `hit:game:${hit.gameSlug}:manage`,
							label: "Manage",
							group: "results",
							requiredRoles: [
								AdminRole.CatalogueWrite,
								AdminRole.CatalogueDeploy,
							],
							perform: (ctx) => {
								ctx.navigate({
									to: "/games/$id/manage",
									params: { id: hit.gameSlug },
								})
								ctx.close()
							},
						},
						{
							id: `hit:game:${hit.gameSlug}:review`,
							label: "Catalogue review",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/catalogue-v2/$gameSlug",
									params: { gameSlug: hit.gameSlug },
								})
								ctx.close()
							},
						},
						{
							id: `hit:game:${hit.gameSlug}:products`,
							label: "Products",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/products",
									search: {
										...PRODUCTS_SEARCH_DEFAULTS,
										gameSlug: hit.gameSlug,
									},
								})
								ctx.close()
							},
						},
						{
							id: `hit:game:${hit.gameSlug}:groups`,
							label: "Expansion groups",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/expansion-groups",
									search: { gameSlug: hit.gameSlug, create: undefined },
								})
								ctx.close()
							},
						},
						{
							id: `hit:game:${hit.gameSlug}:diff`,
							label: "Manage diff",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({ to: "/diff", search: { game: hit.gameSlug } })
								ctx.close()
							},
						},
					]),
			}

		case "expansion": {
			const open = (ctx: PaletteContext) => {
				ctx.navigate({
					to: "/catalogue/games/$gameSlug",
					params: { gameSlug: hit.gameSlug },
					search: { expansion: hit.slug },
				})
				ctx.close()
			}
			return {
				id: `hit:expansion:${hit.gameSlug}:${hit.slug}`,
				label: hit.name ?? hit.slug,
				description: `Expansion · ${hit.gameSlug}${hit.code ? ` · ${hit.code}` : ""}`,
				icon: Box,
				group: "results",
				perform: open,
				submenu: () =>
					step(hit.name ?? hit.slug, [
						{
							id: `hit:expansion:${hit.slug}:backlog`,
							label: "Backlog row",
							group: "results",
							perform: open,
						},
						{
							id: `hit:expansion:${hit.slug}:manage`,
							label: "Manage",
							group: "results",
							requiredRoles: [
								AdminRole.CatalogueWrite,
								AdminRole.CatalogueDeploy,
							],
							perform: (ctx) => {
								ctx.navigate({
									to: "/expansions/$id/manage",
									params: { id: hit.slug },
									search: { gameSlug: hit.gameSlug },
								})
								ctx.close()
							},
						},
						{
							id: `hit:expansion:${hit.slug}:products`,
							label: "Products in expansion",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/products",
									search: {
										...PRODUCTS_SEARCH_DEFAULTS,
										gameSlug: hit.gameSlug,
										expansionSlug: hit.slug,
									},
								})
								ctx.close()
							},
						},
						copyCommand(
							`hit:expansion:${hit.slug}:copy-slug`,
							"Copy slug",
							hit.slug,
						),
						...(hit.code
							? [
									copyCommand(
										`hit:expansion:${hit.slug}:copy-code`,
										"Copy code",
										hit.code,
									),
								]
							: []),
					]),
			}
		}

		case "expansionGroup":
			return {
				id: `hit:expansionGroup:${hit.gameSlug}:${hit.slug}`,
				label: hit.name ?? hit.slug,
				description: `Expansion group · ${hit.gameSlug}`,
				icon: Boxes,
				group: "results",
				perform: (ctx) => {
					ctx.navigate({
						to: "/expansion-groups/$id",
						params: { id: hit.slug },
						search: { gameSlug: hit.gameSlug },
					})
					ctx.close()
				},
				submenu: () =>
					step(hit.name ?? hit.slug, [
						{
							id: `hit:expansionGroup:${hit.slug}:open`,
							label: "Open",
							group: "results",
							perform: (ctx) => {
								ctx.navigate({
									to: "/expansion-groups/$id",
									params: { id: hit.slug },
									search: { gameSlug: hit.gameSlug },
								})
								ctx.close()
							},
						},
						{
							id: `hit:expansionGroup:${hit.slug}:manage`,
							label: "Manage",
							group: "results",
							requiredRoles: [
								AdminRole.CatalogueWrite,
								AdminRole.CatalogueDeploy,
							],
							perform: (ctx) => {
								ctx.navigate({
									to: "/expansion-groups/$id/manage",
									params: { id: hit.slug },
									search: { gameSlug: hit.gameSlug },
								})
								ctx.close()
							},
						},
						copyCommand(
							`hit:expansionGroup:${hit.slug}:copy-slug`,
							"Copy slug",
							hit.slug,
						),
					]),
			}

		case "product": {
			const detailRoute =
				hit.productType === "sealed"
					? ("/products/sealed/$id" as const)
					: ("/products/singles/$id" as const)
			const open = (ctx: PaletteContext) => {
				ctx.navigate({
					to: detailRoute,
					params: { id: hit.slug },
					search: {
						gameSlug: hit.gameSlug,
						expansionSlug: hit.expansionSlug ?? undefined,
					},
				})
				ctx.close()
			}
			return {
				id: `hit:product:${hit.gameSlug}:${hit.slug}`,
				label: hit.name ?? hit.slug,
				description: `${hit.productType === "sealed" ? "Sealed" : "Card"} · ${hit.gameSlug}${hit.printNumber ? ` · #${hit.printNumber}` : ""}${hit.expansionSlug ? ` · ${hit.expansionSlug}` : ""}`,
				icon: Barcode,
				group: "results",
				perform: open,
				submenu: () =>
					step(hit.name ?? hit.slug, [
						{
							id: `hit:product:${hit.slug}:open`,
							label: "Open",
							group: "results",
							perform: open,
						},
						{
							id: `hit:product:${hit.slug}:manage`,
							label: "Manage",
							group: "results",
							requiredRoles: [
								AdminRole.CatalogueWrite,
								AdminRole.CatalogueDeploy,
							],
							perform: (ctx) => {
								ctx.navigate({
									to:
										hit.productType === "sealed"
											? "/products/sealed/$id/manage"
											: "/products/singles/$id/manage",
									params: { id: hit.slug },
									search: {
										gameSlug: hit.gameSlug,
										expansionSlug: hit.expansionSlug ?? undefined,
									},
								})
								ctx.close()
							},
						},
						...(hit.expansionSlug
							? [
									{
										id: `hit:product:${hit.slug}:expansion`,
										label: "Expansion backlog row",
										group: "results" as const,
										perform: (ctx: PaletteContext) => {
											ctx.navigate({
												to: "/catalogue/games/$gameSlug",
												params: { gameSlug: hit.gameSlug },
												search: { expansion: hit.expansionSlug ?? undefined },
											})
											ctx.close()
										},
									},
								]
							: []),
						...(hit.tcgplayerId
							? [
									linkCommand(
										`hit:product:${hit.slug}:tcgplayer`,
										"TCGplayer",
										tcgplayerProductUrl(hit.tcgplayerId),
									),
								]
							: []),
						...(() => {
							const url = hit.cardmarketId
								? cardmarketProductUrl(hit.gameSlug, hit.cardmarketId)
								: null
							return url
								? [
										linkCommand(
											`hit:product:${hit.slug}:cardmarket`,
											"Cardmarket",
											url,
										),
									]
								: []
						})(),
						{
							id: `hit:product:${hit.slug}:marketplace`,
							label: "Open on marketplace ↗",
							group: "results",
							perform: async (ctx) => {
								let raw: unknown
								try {
									raw = await ctx.gameAdminClient.production.getEntity({
										gameSlug: hit.gameSlug,
										entitySlug: hit.slug,
										entityType: "product",
									})
								} catch {
									toast.error("Could not load the production product")
									return
								}
								const parsed = ProductionProductSchema.safeParse(raw)
								if (!parsed.success || !parsed.data.expansion) {
									toast.info("This product is not deployed to production yet")
									return
								}
								window.open(
									marketplaceProductUrl({
										gameSlug: hit.gameSlug,
										expansionSlug: parsed.data.expansion.slug,
										productType: parsed.data.productType,
										nameSlug: parsed.data.nameSlug,
										publicId: parsed.data.publicId,
									}),
									"_blank",
									"noopener",
								)
								ctx.close()
							},
						},
						copyCommand(
							`hit:product:${hit.slug}:copy-slug`,
							"Copy slug",
							hit.slug,
						),
					]),
			}
		}

		case "aiMatcherRun":
			return {
				id: `hit:aiMatcherRun:${hit.id}`,
				label: `AI matcher run · ${hit.gameSlug}${hit.expansionSlug ? ` / ${hit.expansionSlug}` : ""}`,
				icon: FlaskConical,
				group: "results",
				perform: (ctx) => {
					ctx.navigate({
						to: "/catalogue/ai-matcher/$runId",
						params: { runId: hit.id },
					})
					ctx.close()
				},
			}

		case "proposal":
			return {
				id: `hit:proposal:${hit.id}`,
				label: `Proposal · ${hit.title ?? hit.id.slice(-8)}`,
				description: `${hit.gameSlug}${hit.status ? ` · ${hit.status}` : ""}`,
				icon: GitCompareArrows,
				group: "results",
				perform: (ctx) => {
					ctx.navigate({
						to: "/catalogue-v2/proposals/$proposalId",
						params: { proposalId: hit.id },
					})
					ctx.close()
				},
			}
	}
}
